<template>
  <div class="ai-training-terms-panel">
    <b-message v-if="!termTreeGroups.length" type="is-info" class="empty-term-trees">
      {{ $t('no-ontology') }}
    </b-message>
    <div v-for="group in termTreeGroups" :key="group.ontology.id" class="term-tree-group">
      <div class="term-tree-title">
        <span>{{ group.ontology.name }}</span>
        <button v-if="canManageProject" class="delete" type="button" :title="$t('remove-terms-from-image')" @click.stop="removeOntologyFromImage(group.ontology.id)"></button>
      </div>
      <div class="term-tree-rows">
        <annotation-term-row
          v-for="row in group.terms"
          :key="row.term.id"
          :term="row.term"
          :state="termState(row.term)"
          :visible="termVisible(row.term)"
          :terms="terms"
          :images="images"
          :depth="row.depth"
          show-report-selection
          :report-selected="isReportTermSelected(row.term)"
          show-confidence-threshold
          :confidence-threshold="termConfidenceThreshold(row.term)"
          paginated
          :current-page="termState(row.term).page + 1"
          :total="termState(row.term).total"
          :per-page="pageSize"
          @toggle="toggleTerm(row.term)"
          @toggle-visibility="toggleVisibility(row.term)"
          @toggle-report-selection="$emit('toggle-report-term', row.term)"
          @confidence-threshold-change="setTermConfidenceThreshold(row.term, $event)"
          @retry="loadAnnotations(row.term)"
          @page-change="loadAnnotations(row.term, $event - 1)"
          @updateTermsOrTracks="$emit('updateTermsOrTracks', $event)"
          @centerView="$emit('centerView', $event)"
          @delete="$emit('delete', $event)"
          @select="$emit('select', $event)"
        />
      </div>
    </div>

    <annotation-term-row
      class="no-term-group"
      :term="noTerm"
      :state="termState(noTerm)"
      :visible="noTermVisible"
      :terms="terms"
      :images="images"
      paginated
      :current-page="termState(noTerm).page + 1"
      :total="termState(noTerm).total"
      :per-page="pageSize"
      @toggle="toggleTerm(noTerm)"
      @toggle-visibility="toggleVisibility(noTerm)"
      @retry="loadAnnotations(noTerm)"
      @page-change="loadAnnotations(noTerm, $event - 1)"
      @updateTermsOrTracks="$emit('updateTermsOrTracks', $event)"
      @centerView="$emit('centerView', $event)"
      @delete="$emit('delete', $event)"
      @select="$emit('select', $event)"
    />
  </div>
</template>

<script>
import {Annotation, AnnotationCollection} from '@/api';
import Cytomine from '@/api/cytomine';
import AnnotationTermRow from './AnnotationTermRow';
import viewerImageContext from './mixins/viewer-image-context';
import { getAllTerms } from '@/utils/ontology-utils';

const NO_TERM = { id: 'no-term', noTerm: true, name: 'No term', color: '#9CA3AF' };

export default {
  name: 'AiTrainingTermsPanel',
  mixins: [viewerImageContext],
  components: {
    AnnotationTermRow
  },
  props: {
    index: String,
    project: Object,
    refreshToken: {
      type: Number,
      default: 0
    },
    selectedReportTermIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      pageSize: 24,
      contextVersion: 0,
      states: {},
      noTerm: {...NO_TERM, name: this.$t('no-term')}
    };
  },
  computed: {
    imageId() {
      return this.image && this.image.id;
    },
    images() {
      return this.image ? [this.image] : [];
    },
    ontologies() {
      return Array.isArray(this.imageWrapper && this.imageWrapper.ontologies)
        ? this.imageWrapper.ontologies
        : [];
    },
    terms() {
      return this.ontologies.flatMap(ontology => getAllTerms(ontology).map(term => ({
        ...term,
        ontologyId: ontology.id
      })));
    },
    termTreeGroups() {
      return this.ontologies.map(ontology => ({
        ontology,
        terms: this.flattenTerms(ontology.children || [])
      }));
    },
    trainingVisibility() {
      return (this.imageWrapper && this.imageWrapper.aiTrainingVisibility) || {
        hiddenTermIds: [],
        displayNoTerm: true,
        confidenceThresholdsByTerm: {}
      };
    },
    noTermVisible() {
      return this.trainingVisibility.displayNoTerm !== false;
    },
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    }
  },
  watch: {
    imageId(value, oldValue) {
      if (value !== oldValue) {
        this.contextVersion++;
        this.states = {};
      }
    },
    refreshToken() {
      this.refreshExpanded();
    }
  },
  methods: {
    flattenTerms(terms, depth = 0, rows = []) {
      (terms || []).forEach(term => {
        rows.push({term, depth});
        this.flattenTerms(term.children || [], depth + 1, rows);
      });
      return rows;
    },
    stateKey(term) {
      return term.noTerm ? 'no-term' : `${this.imageId}:${term.id}`;
    },
    termState(term) {
      const key = this.stateKey(term);
      if (!this.states[key]) {
        this.$set(this.states, key, {
          term,
          expanded: false,
          loading: false,
          error: false,
          items: [],
          page: 0,
          total: 0,
          hasMore: false,
          requestId: 0
        });
      }
      return this.states[key];
    },
    termVisible(term) {
      if (term.noTerm) {
        return this.noTermVisible;
      }
      return !(this.trainingVisibility.hiddenTermIds || [])
        .some(id => String(id) === String(term.id));
    },
    isReportTermSelected(term) {
      return this.selectedReportTermIds.some(id => String(id) === String(term.id));
    },
    termConfidenceThreshold(term) {
      const value = this.trainingVisibility.confidenceThresholdsByTerm?.[term.id];
      return Number.isFinite(Number(value)) ? Number(value) : 0;
    },
    setTermConfidenceThreshold(term, threshold) {
      this.$store.commit(`${this.imageModule}setAITrainingTermConfidenceThreshold`, {
        termId: term.id,
        threshold
      });
    },
    toggleVisibility(term) {
      if (term.noTerm) {
        this.$store.commit(`${this.imageModule}setAITrainingNoTermVisibility`, !this.noTermVisible);
        return;
      }
      this.$store.commit(`${this.imageModule}toggleAITrainingTermVisibility`, {termId: term.id});
    },
    async removeOntologyFromImage(ontologyId) {
      if (!window.confirm(this.$t('confirm-remove-ontology-from-image'))) {
        return;
      }
      try {
        await this.$store.dispatch(`${this.imageModule}removeOntologyFromImage`, ontologyId);
        this.$notify({type: 'success', text: this.$t('notif-success-remove-ontology-from-image')});
      } catch (error) {
        this.$notify({type: 'error', text: this.$t('notif-error-remove-ontology-from-image')});
      }
    },
    async toggleTerm(term) {
      const state = this.termState(term);
      state.expanded = !state.expanded;
      if (state.expanded && !state.items.length && !state.loading) {
        await this.loadAnnotations(term);
      }
    },
    normalizeAnnotation(raw) {
      const annot = raw instanceof Annotation ? raw : new Annotation(raw);
      const base = `${Cytomine.instance.host}${Cytomine.instance.basePath}`;
      annot.cropURL = annot.cropURL || `${base}annotation/${annot.id}/crop.png`;
      annot.smallCropURL = annot.smallCropURL || `${base}annotation/${annot.id}/crop.png?maxSize=256`;
      return annot;
    },
    async loadAnnotations(term, page = 0) {
      const state = this.termState(term);
      const nextPage = page;
      const context = this.contextVersion;
      const imageId = this.imageId;
      const requestId = ++state.requestId;
      state.loading = true;
      state.error = false;
      try {
        if (!imageId || !this.project || !this.project.id) {
          throw new Error('Missing image or project context.');
        }
        const params = {
          project: this.project.id,
          images: String(imageId),
          noTerm: Boolean(term.noTerm),
          reviewed: false,
          multipleTerm: false,
          showAlgo: true,
          excludeJobLinkedAlgo: true,
          showUser: true,
          showDefault: true,
          showWKT: true,
          showTerm: true,
          showGIS: true
        };
        if (!term.noTerm) {
          params.term = term.id;
        }
        const collection = await new AnnotationCollection({nbPerPage: this.pageSize, ...params}).fetchPage(nextPage);
        if (context !== this.contextVersion || imageId !== this.imageId || requestId !== state.requestId) {
          return;
        }
        const items = collection.array.map(raw => this.normalizeAnnotation(raw));
        state.items = items;
        state.page = nextPage;
        const total = Number(collection.totalNbItems);
        state.total = Number.isFinite(total) ? total : items.length;
        state.hasMore = false;
      } catch (error) {
        if (context === this.contextVersion && imageId === this.imageId && requestId === state.requestId) {
          console.error('Failed to load AI training annotations:', error);
          state.error = true;
          state.hasMore = false;
        }
      } finally {
        if (context === this.contextVersion && imageId === this.imageId && requestId === state.requestId) {
          state.loading = false;
        }
      }
    },
    refreshExpanded() {
      Object.values(this.states).forEach(state => {
        if (state.expanded) {
          state.items = [];
          state.page = 0;
          state.total = 0;
          state.hasMore = false;
          this.loadAnnotations(state.term);
        }
      });
    },
    refreshAnnotations(payload) {
      const idImage = payload && typeof payload === 'object' ? (payload.idImage || payload.image) : payload;
      if (!this.image || (idImage !== undefined && idImage !== null && idImage !== this.image.id)) {
        return;
      }
      this.refreshExpanded();
    }
  },
  mounted() {
    ['addAnnotation', 'reloadAnnotations', 'editAnnotation', 'deleteAnnotation']
      .forEach(event => this.$eventBus.$on(event, this.refreshAnnotations));
  },
  beforeDestroy() {
    ['addAnnotation', 'reloadAnnotations', 'editAnnotation', 'deleteAnnotation']
      .forEach(event => this.$eventBus.$off(event, this.refreshAnnotations));
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.term-tree-group {
  margin-bottom: 0.85rem;
}

.term-tree-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.45rem;
  color: $dark-text-primary;
  font-size: 0.82rem;
  font-weight: 700;
}

.term-tree-rows {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.no-term-group {
  margin-top: 0.85rem;
}
</style>
